import {
    Component,
    EventEmitter,
    Output,
    ViewChild,
    ViewEncapsulation,
    OnInit,
} from '@angular/core';
import {
    FormBuilder,
    Validators,
    FormGroup,
    AbstractControl,
} from '@angular/forms';
import { CreateUserAccountFormData } from '../../model/form-data';
import { CreateAddressSectionComponent } from '../create-address-section/create-address-section.component';
import { CreateProfileComponentService } from '../../services/create.profile.component.service';
import { TranslateService } from '@ngx-translate/core';
import { usernameExistsValidatorFactory } from '../../validators/username-exists-validator';
import { UsernameService } from '../../services/username.service';

@Component({
    selector: 'vb-create-user-profile',
    templateUrl: './create-user-profile.component.html',
    styleUrls: ['./create-user-profile.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class CreateUserProfileComponent implements OnInit {
    isFormValid = true;
    isUserNameUnique: boolean = true;
    submitButtonLabel: string = '';
    userProfileFormGroup!: FormGroup;

    @Output() submitUserProfileFormData =
        new EventEmitter<CreateUserAccountFormData>();
    @Output() selectBusiness = new EventEmitter<any>();
    @Output() selectCharity = new EventEmitter<any>();
    @ViewChild('addressSection')
    private addressSection!: CreateAddressSectionComponent;

    constructor(
        private _formBuilder: FormBuilder,
        private translate: TranslateService,
        private createProfileComponentService: CreateProfileComponentService,
        private usernameService: UsernameService,
    ) {}

    ngOnInit(): void {
        this.userProfileFormGroup = this._formBuilder.group({
            userName: [
                null,
                [
                    Validators.required,
                    Validators.maxLength(30),
                    Validators.pattern(/^[a-zA-Z0-9]*$/),
                ],
                [usernameExistsValidatorFactory(this.usernameService)],
            ],
            firstName: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^[A-Za-z]+( [A-Za-z]+)*$/),
                ],
            ],
            lastName: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^[A-Za-z]+( [A-Za-z]+)*$/),
                ],
            ],
            charitySelected: [false],
            businessSelected: [false],
        });
        this.submitButtonLabel = this.translate.instant('Continue');
        this.userProfileFormGroup.valueChanges.subscribe(() => {
            this.isFormValid = this.validate();
        });
    }

    addressSectionValueChanges(): void {
        this.isFormValid = this.validate();

        this.createProfileComponentService.setFormValidity(this.isFormValid);
    }

    onSubmit(): void {
        if (this.isFormValid) {
            this.submitUserProfileFormData.emit({
                postalCode:
                    this.addressSection.addressFormGroup.controls['postalCode']
                        .value,
                city: this.addressSection.addressFormGroup.controls['city']
                    .value,
                country:
                    this.addressSection.addressFormGroup.controls['country']
                        .value,
                address1:
                    this.addressSection.addressFormGroup.controls['address1']
                        .value,
                address2:
                    this.addressSection.addressFormGroup.controls['address2']
                        .value,
                userName: this.userProfileFormGroup.controls['userName'].value,
                lastname: this.userProfileFormGroup.controls['lastName'].value,
                firstname:
                    this.userProfileFormGroup.controls['firstName'].value,
                province:
                    this.addressSection.addressFormGroup.controls['province']
                        .value,
                phoneNumber:
                    this.addressSection.addressFormGroup.controls['phoneNumber']
                        .value,
                phoneNumberExt: '',
                buzzer: this.addressSection.addressFormGroup.controls['buzzer']
                    .value,
                businessSelected:
                    this.userProfileFormGroup.controls['businessSelected']
                        .value,
                charitySelected:
                    this.userProfileFormGroup.controls['charitySelected'].value,
            });
        }
    }

    validate(): boolean {
        this.addressSection.markAllAsTouched();
        this.userProfileFormGroup.markAllAsTouched();

        if (this.userProfileFormGroup.status === 'PENDING') {
            setTimeout(() => {
                this.isFormValid = this.validate();
            }, 500);
        }
        return (
            this.userProfileFormGroup.valid && this.addressSection.isFormValid()
        );
    }

    resetCheckBoxState(): void {
        this.userProfileFormGroup.controls['charitySelected'].setValue(false);
        this.userProfileFormGroup.controls['businessSelected'].setValue(false);
        this.submitButtonLabel = this.translate.instant('Continue');
    }

    checkBusiness(ev: any): void {
        const isBusinessSelected: boolean =
            this.userProfileFormGroup.controls['businessSelected'].value;
        this.resetCheckBoxState();
        this.userProfileFormGroup.controls['businessSelected'].setValue(
            isBusinessSelected,
        );
        if (this.userProfileFormGroup.controls['businessSelected'].value) {
            this.submitButtonLabel = this.translate.instant(
                'Continue To Business Info',
            );
        }
        this.selectBusiness.emit(ev);
    }

    getHelperText(field: string): string | null {
        const control: AbstractControl<any, any> | null =
            this.userProfileFormGroup.get(field);

        // If no control found, return empty
        if (!control) {
            return null;
        }

        const requiredValidation: boolean =
            control.hasError('required') && control?.touched;
        const maxLengthValidation: boolean =
            control.hasError('maxlength') && control?.touched;
        const usernameExistsValidation: boolean =
            control.hasError('usernameTaken') && control?.touched;
        const patternValidation: boolean =
            control.hasError('pattern') && control?.touched;
        switch (field) {
            case 'userName':
                if (requiredValidation) {
                    return 'Please enter your user name.';
                }
                if (maxLengthValidation) {
                    return `Username can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                if (usernameExistsValidation) {
                    return 'Please enter a unique user name.';
                }
                if (patternValidation) {
                    return 'Username can have only alphabets and numbers';
                }
                break;
            case 'firstName':
                if (requiredValidation) {
                    return 'First Name is required';
                }
                if (maxLengthValidation) {
                    return `First Name can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                if (patternValidation) {
                    return 'First name can have only alphabets, no leading or trailing spaces allowed.';
                }
                break;
            case 'lastName':
                if (requiredValidation) {
                    return 'Last Name is required';
                }
                if (maxLengthValidation) {
                    return `Last Name can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                if (patternValidation) {
                    return 'Last name can have only alphabets, no leading or trailing spaces allowed';
                }
                break;
            default:
                return null;
        }

        return null;
    }

    checkCharity(ev: any): void {
        const isCharitySelected: boolean =
            this.userProfileFormGroup.controls['charitySelected'].value;
        this.resetCheckBoxState();
        this.userProfileFormGroup.controls['charitySelected'].setValue(
            isCharitySelected,
        );
        if (this.userProfileFormGroup.controls['charitySelected'].value) {
            this.submitButtonLabel = this.translate.instant(
                'Continue To Not For Profit Info',
            );
        }
        this.selectCharity.emit(ev);
    }
}
